<template>
  <div class="live-calendar-page">
    <!-- 一、直播日历：有直播的日子高亮，一眼看出哪天播了、播了几场 -->
    <el-card shadow="never" class="card" v-loading="loading">
      <template #header>
        <div class="card-head">
          <span class="card-title">📅 直播日历</span>
          <span class="card-hint">有直播的日子会亮起来 · 点某一天可以看 / 改那天的场次</span>
        </div>
      </template>

      <!-- 工具栏：切月份 + 回今天 + 刷新 + 一行统计 -->
      <div class="toolbar">
        <el-date-picker v-model="monthValue" type="month" value-format="YYYY-MM" :clearable="false"
          placeholder="选月份" style="width: 150px" />
        <el-button @click="goToday">回到今天</el-button>
        <el-button type="primary" plain :loading="loading" @click="loadAll">刷新</el-button>
        <span class="stat">
          一共录了 <b>{{ count }}</b> 场
          <template v-if="first"> · 最早 <b>{{ first }}</b></template>
          <template v-if="last"> · 最近一场 <b>{{ last }}</b></template>
        </span>
      </div>

      <!-- 日历本体：格子里的东西全靠 date-cell 插槽自己画 -->
      <el-calendar v-model="curDate">
        <template #date-cell="{ data }">
          <div class="cal-cell" :class="{ 'has-live': cellCount(data.day) > 0, 'is-out': data.type !== 'current-month' }"
            @click="onCellClick(data)">
            <div class="cell-top">
              <span class="cell-day">{{ dayNumber(data.day) }}</span>
              <span v-if="cellCount(data.day) > 0" class="cell-count">{{ cellCount(data.day) }} 场</span>
            </div>

            <div class="cell-body">
              <div v-for="(row, i) in cellLines(data.day)" :key="i" class="cell-line">{{ preview(row.content) }}</div>
              <div v-if="cellMore(data.day) > 0" class="cell-more">还有 {{ cellMore(data.day) }} 场…</div>
            </div>

            <div v-if="cellTags(data.day).length" class="cell-dots">
              <i v-for="t in cellTags(data.day)" :key="t.name" class="tag-dot" :style="{ background: t.color }"
                :title="t.name"></i>
            </div>
          </div>
        </template>
      </el-calendar>

      <p class="tip">没有直播的日子是白底、不高亮；一天有好几场的时候格子里只画前两场，点进去看全部。</p>
    </el-card>

    <!-- 二、标签管理：名字 + 颜色，改色改名都在这儿 -->
    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🏷 标签管理</span>
          <span class="card-hint">改名字的时候，后端会把已经挂着这个标签的直播记录一起改掉，不用手动调</span>
          <el-button size="small" type="primary" plain @click="openTagAdd">＋ 新增标签</el-button>
        </div>
      </template>

      <el-empty v-if="!tagList.length" description="还没有标签，先加一个「唱歌」试试" :image-size="80" />

      <el-table v-else :data="tagList" border stripe style="width: 100%">
        <el-table-column label="名字" min-width="160">
          <template #default="scope">
            <span class="tag-chip" :style="{ borderColor: scope.row.color, color: scope.row.color }">
              <i class="tag-dot" :style="{ background: scope.row.color }"></i>{{ scope.row.name }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="颜色" width="150">
          <template #default="scope">
            <span class="color-cell"><i class="tag-dot" :style="{ background: scope.row.color }"></i>{{ scope.row.color }}</span>
          </template>
        </el-table-column>
        <el-table-column label="用它的场次" width="120">
          <template #default="scope">{{ tagUseCount(scope.row.name) }} 场</template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template #default="scope">
            <el-button size="small" @click="openTagEdit(scope.row)">改</el-button>
            <el-button v-if="canDelete()" size="small" type="danger" plain @click="deleteTag(scope.row)">删</el-button>
            <span v-else class="no-perm">无删除权限</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 三、「这一天」弹窗：列出当天所有场次，底下能加一场 / 改一场 -->
    <el-dialog v-model="dayDialog" :title="'🗓 ' + (activeDay || '') + ' 的直播'" width="720px" top="8vh">
      <el-empty v-if="!activeRecords.length" description="这天还没有记录，下面加一场吧" :image-size="80" />

      <div v-else class="session-list">
        <div v-for="row in activeRecords" :key="row.id" class="session-item">
          <div class="session-main">
            <div class="session-content">{{ row.content || '（没写内容）' }}</div>
            <div class="session-tags">
              <span v-for="n in (row.tags || [])" :key="n" class="tag-chip"
                :style="{ borderColor: tagColor(n), color: tagColor(n) }">
                <i class="tag-dot" :style="{ background: tagColor(n) }"></i>{{ n }}
              </span>
              <span v-if="!(row.tags || []).length" class="muted">没有标签</span>
            </div>
          </div>
          <div class="session-ops">
            <el-button size="small" @click="openEdit(row)">编辑</el-button>
            <el-button v-if="canDelete()" size="small" type="danger" plain @click="deleteSession(row)">删除</el-button>
            <span v-else class="no-perm">无删除权限</span>
          </div>
        </div>
      </div>

      <div class="form-zone">
        <el-button v-if="!formVisible" type="primary" plain @click="openAdd">＋ 加一场</el-button>

        <!-- 加一场和改一场共用这张表单，有没有 id 决定是新增还是改 -->
        <el-form v-else label-width="70px" class="session-form">
          <el-form-item label="日期">
            <el-date-picker v-model="form.day" type="date" value-format="YYYY-MM-DD" :clearable="false"
              placeholder="选日期" style="width: 180px" />
            <span v-if="form.id" class="tip">改日期会把这一场挪到那天去</span>
          </el-form-item>
          <el-form-item label="内容">
            <el-input v-model="form.content" type="textarea" :rows="3" maxlength="500" show-word-limit
              placeholder="这场播了啥，随便写点，空着也行（标签得勾一个）" />
          </el-form-item>
          <el-form-item label="标签">
            <el-checkbox-group v-model="form.tags">
              <el-checkbox v-for="t in tagList" :key="t.id" :label="t.name">{{ t.name }}</el-checkbox>
            </el-checkbox-group>
            <div v-if="!tagList.length" class="muted">还没有标签，去下面的「标签管理」加几个</div>
          </el-form-item>
          <el-form-item label="录播链接">
            <el-input v-model="form.replay" maxlength="500" show-word-limit clearable
              placeholder="有录播就把链接粘在这儿（http/https），没有留空" style="max-width: 520px" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" :loading="saving" @click="saveSession">保存</el-button>
            <el-button @click="cancelForm">取消</el-button>
            <span class="tip">内容可以空着，标签得勾一个</span>
          </el-form-item>
        </el-form>
      </div>
    </el-dialog>

    <!-- 四、标签新增 / 改名改色 -->
    <el-dialog v-model="tagDialog" :title="tagForm.id ? '改标签' : '新增标签'" width="420px">
      <el-form label-width="60px">
        <el-form-item label="名字">
          <el-input v-model="tagForm.name" maxlength="16" placeholder="例如 唱歌" />
        </el-form-item>
        <el-form-item label="颜色">
          <el-color-picker v-model="tagForm.color" />
          <span class="tag-chip" :style="{ borderColor: tagForm.color, color: tagForm.color }">
            <i class="tag-dot" :style="{ background: tagForm.color }"></i>{{ tagForm.name || '预览' }}
          </span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="tagDialog = false">取消</el-button>
        <el-button type="primary" :loading="tagSaving" @click="saveTag">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { canDelete } from '../../../util/permission';

/* ---------------- 小工具 ---------------- */
const pad = (n) => (n < 10 ? '0' + n : '' + n);
const toMonthStr = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1);

/* ---------------- 状态 ---------------- */
const curDate = ref(new Date());                 // 日历当前选中的那天（el-calendar 的 v-model）
const monthValue = ref(toMonthStr(new Date()));  // 工具栏月份选择器的值，'YYYY-MM'
const loading = ref(false);

const days = ref([]);        // 所有直播记录（接口一次全给，前端按天分桶）
const tagList = ref([]);     // 标签库
const first = ref('');
const last = ref('');
const count = ref(0);

/* ---------------- 日期 → 记录 ---------------- */
const dayMap = computed(() => {
  const m = {};
  days.value.forEach((d) => {
    if (!m[d.day]) m[d.day] = [];
    m[d.day].push(d);
  });
  // 同一天有好几场时，按 sort 排，sort 一样就按 id，顺序稳定看着不跳
  Object.keys(m).forEach((k) => {
    m[k].sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || (Number(a.id) || 0) - (Number(b.id) || 0));
  });
  return m;
});

const recordsOf = (day) => dayMap.value[day] || [];
const cellCount = (day) => recordsOf(day).length;
const cellLines = (day) => recordsOf(day).slice(0, 2);
const cellMore = (day) => Math.max(0, cellCount(day) - 2);

const preview = (text) => {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return '（只打了标签）';
  return s.length > 8 ? s.slice(0, 8) + '…' : s;
};

const dayNumber = (day) => String(day || '').slice(8).replace(/^0/, '');

/** 标签名 → 颜色，标签被删掉之后老记录还可能留着名字，兜底给主色 */
const tagColor = (name) => {
  const t = tagList.value.find((x) => x.name === name);
  return (t && t.color) || '#409eff';
};

/** 格子里最多画 3 个标签小圆点，多了会挤 */
const cellTags = (day) => {
  const names = [];
  recordsOf(day).forEach((row) => {
    (row.tags || []).forEach((n) => { if (names.indexOf(n) < 0) names.push(n); });
  });
  return names.slice(0, 3).map((n) => ({ name: n, color: tagColor(n) }));
};

/* ---------------- 拉数据 ---------------- */
const loadLive = async () => {
  // 带个时间戳：改完/删完再刷新时，别让浏览器把上一次的 GET 结果直接拿来用
  const res = await axios.get('/adminapi/live/list?_=' + Date.now());
  const d = res.data || {};
  if (d.code !== 200) {
    ElMessage.error(d.message || '直播记录读取失败');
    return;
  }
  days.value = d.days || [];
  first.value = d.first || '';
  last.value = d.last || '';
  count.value = Number(d.count) || 0;
};

const loadTags = async () => {
  const res = await axios.get('/adminapi/live/tag/list?_=' + Date.now());
  const d = res.data || {};
  if (d.code !== 200) {
    ElMessage.error(d.message || '标签读取失败');
    return;
  }
  tagList.value = d.tags || [];
};

/** 日历把整月的数据一次性拿全了，所以切月份不用再请求，只要翻页就行 */
const loadAll = async () => {
  loading.value = true;
  try {
    await Promise.all([loadLive(), loadTags()]);
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message);
  } finally {
    loading.value = false;
  }
};

/* ---------------- 月份切换 ---------------- */
// 选月份 → 把日历翻到那个月 1 号（el-calendar 靠选中日期来判断显示哪个月）
watch(monthValue, (mv) => {
  if (!mv || toMonthStr(curDate.value) === mv) return;
  const parts = mv.split('-').map(Number);
  curDate.value = new Date(parts[0], parts[1] - 1, 1);
});

// 反过来：在日历里点「上个月 / 下个月 / 今天」时，工具栏的月份跟着变
watch(curDate, (d) => {
  const mv = toMonthStr(d);
  if (mv !== monthValue.value) monthValue.value = mv;
});

const goToday = () => {
  curDate.value = new Date();
  monthValue.value = toMonthStr(new Date());
};

/* ---------------- 「这一天」弹窗 ---------------- */
const dayDialog = ref(false);
const activeDay = ref('');
const activeRecords = computed(() => recordsOf(activeDay.value));

const onCellClick = (data) => {
  // 上下月补白的那几格点了会让日历翻页，这里不弹窗，免得主人看花眼
  if (data.type !== 'current-month') return;
  openDay(data.day);
};

const openDay = (day) => {
  activeDay.value = day;
  formVisible.value = false;
  resetForm(day);
  dayDialog.value = true;
};

/* ---------------- 场次表单 ---------------- */
const formVisible = ref(false);
const saving = ref(false);
const form = ref({ id: null, day: '', content: '', replay: '', tags: [], sort: undefined });

const resetForm = (day) => {
  form.value = { id: null, day: day || '', content: '', replay: '', tags: [], sort: undefined };
};

const openAdd = () => {
  resetForm(activeDay.value);
  formVisible.value = true;
};

const openEdit = (row) => {
  form.value = {
    id: row.id,
    day: row.day,
    content: row.content || '',
    replay: row.replay || '',
    tags: (row.tags || []).slice(),
    sort: row.sort,
  };
  formVisible.value = true;
};

const cancelForm = () => {
  formVisible.value = false;
  resetForm(activeDay.value);
};

const saveSession = async () => {
  const day = String(form.value.day || '').trim();
  const content = String(form.value.content || '').trim();
  if (!day) {
    ElMessage.warning('先选个日期');
    return;
  }
  if (!form.value.tags.length) {
    ElMessage.warning('标签得勾一个');
    return;
  }
  saving.value = true;
  try {
    const body = { day, content: form.value.content, replay: form.value.replay, tags: form.value.tags };
    if (form.value.id) body.id = form.value.id;
    if (form.value.sort !== undefined && form.value.sort !== null) body.sort = form.value.sort;

    const res = await axios.post('/adminapi/live/save', body);
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已保存');
      formVisible.value = false;
      await loadAll();
      // 日期被改过的话，这一场已经不在原来那天了，弹窗跟着跳到新日期
      activeDay.value = day;
      resetForm(day);
    } else {
      ElMessage.error(d.message || '保存失败');
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message);
  } finally {
    saving.value = false;
  }
};

const deleteSession = async (row) => {
  try {
    await ElMessageBox.confirm('确定删掉 ' + row.day + ' 这一场吗？删掉就找不回来了', '提示', { type: 'warning' });
  } catch (e) {
    return;   // 主人点了取消
  }
  try {
    const res = await axios.post('/adminapi/live/delete', { id: row.id });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已删除');
      await loadAll();
    } else {
      ElMessage.error(d.message || '删除失败');
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message);
  }
};

/* ---------------- 标签管理 ---------------- */
const tagDialog = ref(false);
const tagSaving = ref(false);
const tagForm = ref({ id: null, name: '', color: '#409eff', sort: undefined });

/** 本地数一下这个标签挂了多少场，删之前好告诉主人会摘掉几条 */
const tagUseCount = (name) => days.value.filter((d) => (d.tags || []).indexOf(name) >= 0).length;

const openTagAdd = () => {
  tagForm.value = { id: null, name: '', color: '#ff8fab', sort: undefined };
  tagDialog.value = true;
};

const openTagEdit = (tag) => {
  tagForm.value = { id: tag.id, name: tag.name, color: tag.color || '#409eff', sort: tag.sort };
  tagDialog.value = true;
};

const saveTag = async () => {
  const name = String(tagForm.value.name || '').trim();
  if (!name) {
    ElMessage.warning('标签得有个名字');
    return;
  }
  tagSaving.value = true;
  try {
    const body = { name, color: tagForm.value.color || '#409eff' };
    if (tagForm.value.id) body.id = tagForm.value.id;
    if (tagForm.value.sort !== undefined && tagForm.value.sort !== null) body.sort = tagForm.value.sort;

    const res = await axios.post('/adminapi/live/tag/save', body);
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已保存');
      tagDialog.value = false;
      // 改名后台会把记录里的旧名字一起换掉，所以两边的数据都得重拉
      await loadAll();
    } else {
      ElMessage.error(d.message || '保存失败');
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message);
  } finally {
    tagSaving.value = false;
  }
};

const deleteTag = async (tag) => {
  const used = tagUseCount(tag.name);
  const extra = used > 0 ? '，已经挂在 ' + used + ' 场直播上的这个标签也会一起摘掉' : '（现在还没有直播用它）';
  try {
    await ElMessageBox.confirm('确定删掉标签「' + tag.name + '」吗' + extra + '？', '提示', { type: 'warning' });
  } catch (e) {
    return;
  }
  try {
    const res = await axios.post('/adminapi/live/tag/delete', { id: tag.id });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已删除');
      await loadAll();
    } else {
      ElMessage.error(d.message || '删除失败');
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message);
  }
};

onMounted(loadAll);
</script>

<style scoped>
.live-calendar-page { padding: 4px; }
.card { margin-bottom: 16px; }
.card-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.card-title { font-size: 15px; font-weight: 600; }
.card-hint { font-size: 12px; color: #909399; margin-right: auto; }
.tip { font-size: 12px; color: #909399; margin-left: 8px; }
.muted { font-size: 12px; color: #c0c4cc; }
.no-perm { font-size: 12px; color: #c0c4cc; margin-left: 4px; }

.toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.toolbar .stat { font-size: 13px; color: #606266; }
.toolbar .stat b { color: #409eff; }

/* 格子：日历自带的高度不够放两行字，往下撑一点 */
:deep(.el-calendar-table .el-calendar-day) { height: 96px; padding: 4px; overflow: hidden; }
:deep(.el-calendar-table td.is-today .el-calendar-day) { background: #fff8e6; }

.cal-cell { height: 100%; padding: 4px 6px; border-radius: 6px; border: 1px solid transparent; display: flex; flex-direction: column; gap: 3px; overflow: hidden; cursor: pointer; transition: background .15s, border-color .15s; box-sizing: border-box; }
.cal-cell:hover { border-color: #c6e2ff; background: #f5faff; }
/* 有直播的日子才给底色，扫一眼就知道哪天播了 */
.cal-cell.has-live { background: #ecf5ff; border-color: #d9ecff; }
.cal-cell.has-live:hover { border-color: #409eff; }
.cal-cell.is-out { opacity: .4; }

.cell-top { display: flex; align-items: center; justify-content: space-between; gap: 4px; }
.cell-day { font-size: 13px; font-weight: 600; color: #303133; }
.cell-count { font-size: 11px; color: #fff; background: #409eff; border-radius: 8px; padding: 0 5px; line-height: 16px; }
.cell-body { flex: 1; overflow: hidden; }
.cell-line { font-size: 12px; color: #606266; line-height: 17px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cell-more { font-size: 11px; color: #909399; }
.cell-dots { display: flex; align-items: center; gap: 4px; }

.tag-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
.tag-chip { display: inline-flex; align-items: center; gap: 5px; padding: 1px 8px; border: 1px solid #dcdfe6; border-radius: 10px; font-size: 12px; line-height: 18px; background: #fff; }
.color-cell { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #606266; }

.session-list { display: flex; flex-direction: column; gap: 10px; max-height: 42vh; overflow-y: auto; }
.session-item { display: flex; justify-content: space-between; gap: 12px; padding: 10px 12px; border: 1px solid #ebeef5; border-radius: 8px; background: #fafcff; }
.session-main { flex: 1; min-width: 0; }
.session-content { font-size: 13px; color: #303133; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
.session-tags { margin-top: 6px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.session-ops { flex: 0 0 auto; display: flex; align-items: flex-start; }

.form-zone { margin-top: 14px; padding-top: 12px; border-top: 1px dashed #ebeef5; }
.session-form { margin-top: 4px; }
</style>
